import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { apiClient } from '../../api/client';
import {
  buildPatientPatchPayload,
  emptyIntakeFormState,
  patientToFormState,
  type Patient,
  type PatientIntakeFormState,
  type PatientStatus,
} from '../../constants/patient';

const AUTOSAVE_MS = 30000;

export function usePatientIntakeForm(patientId: number | null) {
  const [form, setForm] = useState<PatientIntakeFormState>(emptyIntakeFormState);
  const [id, setId] = useState<number | null>(patientId);
  const [loading, setLoading] = useState(Boolean(patientId));
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [status, setStatus] = useState<PatientStatus | null>(patientId ? null : 'draft');
  const formRef = useRef(form);
  const idRef = useRef(id);
  const dirtyRef = useRef(false);

  formRef.current = form;
  idRef.current = id;

  const patchForm = useCallback((partial: Partial<PatientIntakeFormState>) => {
    dirtyRef.current = true;
    setForm((prev) => ({ ...prev, ...partial }));
  }, []);

  const saveDraft = useCallback(async (): Promise<number | null> => {
    setSaving(true);
    setSaveError('');
    const payload = buildPatientPatchPayload(formRef.current);
    try {
      if (idRef.current == null) {
        const res = await apiClient.post<Patient>('/api/patients', payload);
        setId(res.data.id);
        idRef.current = res.data.id;
        setStatus(res.data.status);
        dirtyRef.current = false;
        setLastSavedAt(new Date());
        return res.data.id;
      }
      await apiClient.patch(`/api/patients/${idRef.current}`, payload);
      dirtyRef.current = false;
      setLastSavedAt(new Date());
      return idRef.current;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setSaveError(String(e.response.data.error));
      } else {
        setSaveError('保存に失敗しました');
      }
      return null;
    } finally {
      setSaving(false);
    }
  }, []);

  const completeIntake = useCallback(async (): Promise<boolean> => {
    const savedId = await saveDraft();
    if (savedId == null) return false;
    try {
      await apiClient.post(`/api/patients/${savedId}/complete`);
      return true;
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string; errors?: string[] } | undefined;
        setSaveError(data?.errors?.join(' ') ?? data?.error ?? '受付完了に失敗しました');
      } else {
        setSaveError('受付完了に失敗しました');
      }
      return false;
    }
  }, [saveDraft]);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await apiClient.get<Patient>(`/api/patients/${patientId}`);
        setForm(patientToFormState(res.data));
        setId(res.data.id);
        setStatus(res.data.status);
        idRef.current = res.data.id;
      } catch {
        setLoadError('患者情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [patientId]);

  // 初回の手動保存後のみ自動保存（新規作成時は id が付与されるまで走らない）
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (dirtyRef.current && idRef.current != null) {
        void saveDraft();
      }
    }, AUTOSAVE_MS);
    return () => window.clearInterval(timer);
  }, [saveDraft]);

  // 未保存の入力があるままタブを閉じる／再読み込みした場合に警告する
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  return {
    form,
    patchForm,
    setForm,
    id,
    loading,
    loadError,
    saving,
    saveError,
    setSaveError,
    lastSavedAt,
    status,
    saveDraft,
    completeIntake,
  };
}
